import { escapeHtml } from "../router.js";
import { fmtPct } from "../format.js";

/**
 * Inline SVG gauge (semi-circular arc) for a 0..1 rate.
 * opts: { label, sublabel, n, badge: {text, variant} | null, medianHouse, medianParty }
 */
export function renderGauge(value, opts = {}) {
  const v = value === null || value === undefined ? null : Math.max(0, Math.min(1, Number(value)));
  const r = 44;
  const cx = 60;
  const cy = 58;
  const arc = (from, to, cls) => {
    const a0 = Math.PI * (1 - from);
    const a1 = Math.PI * (1 - to);
    const x0 = cx + r * Math.cos(a0);
    const y0 = cy - r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1);
    const y1 = cy - r * Math.sin(a1);
    const large = to - from > 0.5 ? 1 : 0;
    return `<path class="${cls}" d="M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}" />`;
  };
  const tick = (p, cls, len = 8) => {
    if (p === null || p === undefined) return "";
    const a = Math.PI * (1 - Math.max(0, Math.min(1, p)));
    const x0 = cx + (r - len / 2 - 5) * Math.cos(a);
    const y0 = cy - (r - len / 2 - 5) * Math.sin(a);
    const x1 = cx + (r + len / 2 + 5) * Math.cos(a);
    const y1 = cy - (r + len / 2 + 5) * Math.sin(a);
    return `<line class="${cls}" x1="${x0.toFixed(2)}" y1="${y0.toFixed(2)}" x2="${x1.toFixed(2)}" y2="${y1.toFixed(2)}" />`;
  };
  const variant = v === null ? "neutral" : v >= 0.8 ? "ok" : v >= 0.5 ? "warn" : "no";
  const badge = opts.badge
    ? `<span class="pill pill--${escapeHtml(opts.badge.variant || "neutral")} gauge__badge">${escapeHtml(opts.badge.text)}</span>`
    : "";
  return `
    <div class="gauge gauge--${variant}">
      <div class="gauge__head">
        <span class="stat-label">${escapeHtml(opts.label || "")}</span>
        ${badge}
      </div>
      <svg viewBox="0 0 120 70" class="gauge__svg" role="img" aria-label="${escapeHtml(opts.label || "")}: ${fmtPct(v)}">
        ${arc(0, 1, "gauge__track")}
        ${v !== null && v > 0 ? arc(0, v, "gauge__value") : ""}
        ${tick(opts.medianHouse, "gauge__tick gauge__tick--house")}
        ${tick(opts.medianParty, "gauge__tick gauge__tick--party", 6)}
        <text x="${cx}" y="${cy - 2}" text-anchor="middle" class="gauge__number">${v === null ? "—" : fmtPct(v)}</text>
      </svg>
      <div class="gauge__foot">
        ${opts.n !== undefined && opts.n !== null ? `<span class="meta-muted">${escapeHtml(opts.n.toLocaleString("pt-BR"))} votações</span>` : ""}
        ${opts.medianHouse !== undefined && opts.medianHouse !== null ? `<span class="legend legend--house">mediana da casa ${fmtPct(opts.medianHouse)}</span>` : ""}
        ${opts.medianParty !== undefined && opts.medianParty !== null ? `<span class="legend legend--party">mediana do partido ${fmtPct(opts.medianParty)}</span>` : ""}
        ${opts.sublabel ? `<span class="meta-muted">${escapeHtml(opts.sublabel)}</span>` : ""}
      </div>
    </div>`;
}

/** Horizontal stacked bar of vote counts (sim / não / abstenção / obstrução / outros). */
export function renderCountsBar(rc) {
  const parts = [
    ["sim", rc.votes_yes, "Sim"],
    ["nao", rc.votes_no, "Não"],
    ["abstencao", rc.votes_abstain, "Abstenção"],
    ["obstrucao", rc.votes_obstruction, "Obstrução"],
    ["outro", rc.votes_other, "Outros"],
  ].filter(([, n]) => n);
  const total = parts.reduce((s, [, n]) => s + Number(n || 0), 0) || rc.votes_total || 0;
  if (!total) return `<p class="muted">Sem contagem de votos (votação simbólica ou secreta).</p>`;
  const segs = parts
    .map(([k, n]) => `<span class="cbar__seg cbar__seg--${k}" style="flex-grow:${Number(n)}" title="${escapeHtml(k)}: ${n}"></span>`)
    .join("");
  const legend = parts
    .map(([k, n, label]) => `<span class="total-pill total-pill--${k}"><strong>${n}</strong> ${label}</span>`)
    .join("");
  return `<div class="cbar" role="img" aria-label="Distribuição dos votos">${segs}</div><div class="totals">${legend}<span class="total-pill"><strong>${total}</strong> total</span></div>`;
}
